import { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ChevronLeft, ChevronRight, Download, Loader2, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';

const MIN_SCALE = 1;
const MAX_SCALE = 6;
const WHEEL_ZOOM_STEP = 0.0025; // multiplicateur par pixel de delta

/**
 * Lightbox d'images (fullscreen) avec navigation clavier ← → et Échap.
 * Zoom molette / trackpad / pinch, pan à la souris/tactile quand zoomé.
 * Double-clic ou touche "0" pour reset.
 * `images`: [{ id, url?, loader?, filename? }]
 */
export function ImageLightbox({ open, images, initialIndex = 0, onClose }) {
    const [index, setIndex] = useState(initialIndex);
    const [blobs, setBlobs] = useState({});
    const [loadingIdx, setLoadingIdx] = useState(false);

    // Zoom / pan state
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);

    const containerRef = useRef(null);
    const isPanningRef = useRef(false);
    const panStartRef = useRef({ x: 0, y: 0, tx: 0, ty: 0 });
    const pinchRef = useRef(null); // { startDist, startScale }

    const resetZoom = useCallback(() => {
        setScale(1);
        setTx(0);
        setTy(0);
    }, []);

    useEffect(() => {
        if (open) {
            setIndex(initialIndex);
            resetZoom();
        }
    }, [open, initialIndex, resetZoom]);

    // Reset zoom quand on change d'image
    useEffect(() => {
        resetZoom();
    }, [index, resetZoom]);

    const total = images?.length || 0;
    const current = images?.[index];

    const ensureLoaded = useCallback(async (i) => {
        if (i < 0 || i >= total) return;
        const item = images[i];
        if (!item || item.url || blobs[item.id]) return;
        if (!item.loader) return;
        try {
            const url = await item.loader();
            setBlobs((prev) => ({ ...prev, [item.id]: url }));
        } catch { /* silencieux */ }
    }, [images, blobs, total]);

    useEffect(() => {
        if (!open || !current) return;
        setLoadingIdx(true);
        ensureLoaded(index).finally(() => setLoadingIdx(false));
        ensureLoaded(index + 1);
        ensureLoaded(index - 1);
    }, [open, index, current, ensureLoaded]);

    // Navigation clavier + zoom clavier
    useEffect(() => {
        if (!open) return;
        const handler = (e) => {
            if (e.key === 'Escape') onClose?.();
            else if (e.key === 'ArrowLeft') { if (scale <= 1.01) setIndex((i) => Math.max(0, i - 1)); }
            else if (e.key === 'ArrowRight') { if (scale <= 1.01) setIndex((i) => Math.min(total - 1, i + 1)); }
            else if (e.key === '+' || e.key === '=') setScale((s) => Math.min(MAX_SCALE, s * 1.2));
            else if (e.key === '-' || e.key === '_') setScale((s) => {
                const next = Math.max(MIN_SCALE, s / 1.2);
                if (next <= 1.001) { setTx(0); setTy(0); return 1; }
                return next;
            });
            else if (e.key === '0') resetZoom();
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [open, total, onClose, scale, resetZoom]);

    // Zoom molette / pinch trackpad (ctrl+wheel envoyé par les trackpads)
    // Écouté en non-passif sur le container pour pouvoir preventDefault
    useEffect(() => {
        const el = containerRef.current;
        if (!el || !open) return;
        const onWheel = (e) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            const cx = e.clientX - rect.left - rect.width / 2;
            const cy = e.clientY - rect.top - rect.height / 2;
            // Delta positif => zoom out (scroll bas), négatif => zoom in
            const factor = Math.exp(-e.deltaY * WHEEL_ZOOM_STEP);
            setScale((prevScale) => {
                const newScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, prevScale * factor));
                if (newScale === prevScale) return prevScale;
                // Ajuste la translation pour zoomer autour du curseur
                const ratio = newScale / prevScale;
                setTx((prevTx) => (prevTx - cx) * ratio + cx);
                setTy((prevTy) => (prevTy - cy) * ratio + cy);
                if (newScale <= 1.001) { setTx(0); setTy(0); return 1; }
                return newScale;
            });
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [open]);

    if (!open || !current) return null;

    const displayUrl = current.url || blobs[current.id];
    const canPrev = index > 0;
    const canNext = index < total - 1;
    const isZoomed = scale > 1.001;

    const doDownload = async () => {
        if (!displayUrl) return;
        const a = document.createElement('a');
        a.href = displayUrl;
        a.download = current.filename || `image-${index + 1}`;
        document.body.appendChild(a); a.click(); a.remove();
    };

    // Pan souris
    const onMouseDown = (e) => {
        if (!isZoomed) return;
        e.preventDefault();
        isPanningRef.current = true;
        panStartRef.current = { x: e.clientX, y: e.clientY, tx, ty };
    };
    const onMouseMove = (e) => {
        if (!isPanningRef.current) return;
        setTx(panStartRef.current.tx + (e.clientX - panStartRef.current.x));
        setTy(panStartRef.current.ty + (e.clientY - panStartRef.current.y));
    };
    const stopPan = () => { isPanningRef.current = false; };

    // Pinch tactile
    const dist = (t1, t2) => Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
    const onTouchStart = (e) => {
        if (e.touches.length === 2) {
            pinchRef.current = { startDist: dist(e.touches[0], e.touches[1]), startScale: scale };
        } else if (e.touches.length === 1 && isZoomed) {
            isPanningRef.current = true;
            panStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY, tx, ty };
        }
    };
    const onTouchMove = (e) => {
        if (e.touches.length === 2 && pinchRef.current) {
            e.preventDefault();
            const d = dist(e.touches[0], e.touches[1]);
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, pinchRef.current.startScale * (d / pinchRef.current.startDist)));
            if (next <= 1.001) { setScale(1); setTx(0); setTy(0); } else setScale(next);
        } else if (e.touches.length === 1 && isPanningRef.current) {
            setTx(panStartRef.current.tx + (e.touches[0].clientX - panStartRef.current.x));
            setTy(panStartRef.current.ty + (e.touches[0].clientY - panStartRef.current.y));
        }
    };
    const onTouchEnd = (e) => {
        if (e.touches.length < 2) pinchRef.current = null;
        if (e.touches.length === 0) isPanningRef.current = false;
    };

    const onDoubleClick = (e) => {
        e.stopPropagation();
        if (isZoomed) resetZoom();
        else setScale(2.5);
    };

    const zoomBy = (factor) => {
        setScale((s) => {
            const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, s * factor));
            if (next <= 1.001) { setTx(0); setTy(0); return 1; }
            return next;
        });
    };

    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center"
            onClick={onClose}
            data-testid="image-lightbox"
        >
            {/* Top bar */}
            <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-4 py-3 text-white z-10" onClick={(e) => e.stopPropagation()}>
                <div className="text-sm">
                    {current.filename && <span className="mr-2 opacity-80">{current.filename}</span>}
                    <span className="text-xs bg-white/10 rounded-full px-2 py-0.5">{index + 1} / {total}</span>
                    {isZoomed && (
                        <span className="ml-2 text-xs bg-white/10 rounded-full px-2 py-0.5" data-testid="lightbox-zoom-level">
                            {Math.round(scale * 100)}%
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => zoomBy(1 / 1.25)}
                        disabled={!isZoomed}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Dézoomer (-)"
                        data-testid="lightbox-zoom-out"
                    >
                        <ZoomOut className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => zoomBy(1.25)}
                        disabled={scale >= MAX_SCALE - 0.01}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Zoomer (+)"
                        data-testid="lightbox-zoom-in"
                    >
                        <ZoomIn className="w-4 h-4" />
                    </button>
                    {isZoomed && (
                        <button
                            onClick={resetZoom}
                            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            title="Réinitialiser le zoom (0)"
                            data-testid="lightbox-zoom-reset"
                        >
                            <RotateCcw className="w-4 h-4" />
                        </button>
                    )}
                    {displayUrl && (
                        <button
                            onClick={doDownload}
                            className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                            title="Télécharger"
                            data-testid="lightbox-download"
                        >
                            <Download className="w-4 h-4" />
                        </button>
                    )}
                    <button
                        onClick={onClose}
                        className="w-9 h-9 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                        title="Fermer (Échap)"
                        data-testid="lightbox-close"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Nav prev */}
            {canPrev && !isZoomed && (
                <button
                    onClick={(e) => { e.stopPropagation(); setIndex((i) => i - 1); }}
                    className="absolute left-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                    title="Précédent (←)"
                    data-testid="lightbox-prev"
                    aria-label="Image précédente"
                >
                    <ChevronLeft className="w-5 h-5" />
                </button>
            )}
            {canNext && !isZoomed && (
                <button
                    onClick={(e) => { e.stopPropagation(); setIndex((i) => i + 1); }}
                    className="absolute right-4 z-10 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
                    title="Suivant (→)"
                    data-testid="lightbox-next"
                    aria-label="Image suivante"
                >
                    <ChevronRight className="w-5 h-5" />
                </button>
            )}

            {/* Image container */}
            <div
                ref={containerRef}
                className="w-full h-full flex items-center justify-center overflow-hidden touch-none"
                onClick={(e) => e.stopPropagation()}
                onDoubleClick={onDoubleClick}
                onMouseDown={onMouseDown}
                onMouseMove={onMouseMove}
                onMouseUp={stopPan}
                onMouseLeave={stopPan}
                onTouchStart={onTouchStart}
                onTouchMove={onTouchMove}
                onTouchEnd={onTouchEnd}
                style={{ cursor: isZoomed ? (isPanningRef.current ? 'grabbing' : 'grab') : 'zoom-in' }}
            >
                {displayUrl ? (
                    <img
                        src={displayUrl}
                        alt={current.filename || `Image ${index + 1}`}
                        className={cn(
                            'max-w-[95vw] max-h-[90vh] object-contain select-none',
                            loadingIdx ? 'opacity-70' : 'opacity-100',
                            isPanningRef.current ? '' : 'transition-transform duration-100 ease-out',
                        )}
                        style={{
                            transform: `translate3d(${tx}px, ${ty}px, 0) scale(${scale})`,
                            transformOrigin: 'center center',
                            willChange: 'transform',
                        }}
                        draggable={false}
                        data-testid="lightbox-image"
                    />
                ) : (
                    <div className="text-white flex items-center gap-2">
                        <Loader2 className="w-6 h-6 animate-spin" />
                        <span>Chargement…</span>
                    </div>
                )}
            </div>

            {/* Hint zoom */}
            {!isZoomed && displayUrl && (
                <div className="absolute bottom-16 left-1/2 -translate-x-1/2 text-white/60 text-xs bg-black/40 backdrop-blur rounded-full px-3 py-1 pointer-events-none" data-testid="lightbox-zoom-hint">
                    Molette / pincez pour zoomer · Double-clic pour agrandir
                </div>
            )}

            {/* Bottom thumbnails (si plusieurs) */}
            {total > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 max-w-[80vw] overflow-x-auto px-4 py-2 bg-black/40 backdrop-blur rounded-full z-10" onClick={(e) => e.stopPropagation()}>
                    {images.map((it, i) => (
                        <button
                            key={it.id || i}
                            onClick={() => setIndex(i)}
                            className={cn(
                                'w-1.5 h-1.5 rounded-full transition-all',
                                i === index ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/80',
                            )}
                            aria-label={`Aller à l'image ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>,
        document.body,
    );
}
